import { fromJS } from 'immutable';
import * as types from '../constants';

const initialState = fromJS({
  calendar: [],
  calendarMain: [],
  calendarPre: [],
  calendarLive: [],
  calendarAfterLive: [],
  calendarFull: [],
  calendarDelay: [],
  calendarPost: [],
  calendarAbandoned: [],
  calendarSuspended: [],
  teamCalendarWidget: [],
  calendars: {},
  cupTabbars: {},
});

/**
 * Contains teamIDs of italian teams.
 *
 * @const
 * @type {Array}
 */
const italianTeams = [
  '119', // Italy
  '120', // Milan
  '121', // Roma
  '123', // Bologna
  '124', // Cagliari
  '125', // Fiorentina
  '126', // Verona
  '127', // Inter
  '128', // Juventus
  '129', // Lazio
  '130', // Lecce
  '131', // Parma
  '135', // Torino
  '136', // Udinese
  '456', // Atalanta
  '458', // Brescia
  '459', // Napoli
  '603', // Sampdoria
  '990', // Genoa
  '1454', // Spal
  '2105', // Italy U21
  '2182', // Sassuolo
  '6328', // Italy Women
];

/**
  * Sorts prematch matches by start date/time.
  * @param {Object} items
  * @return {Object}
*/
function sortByDateTime(items) {
  return items.sort((a, b) => {
    const dateA = new Date(a.get('date')).getTime();
    const dateB = new Date(b.get('date')).getTime();
    if (a.get('status') === 'PREMATCH') {
      return (dateA > dateB) ? 1 : -1;
    }
    return 0;
  });
}

/**
  * Retrieves match's ending date/time based on specific fields.
  * @param {Object} games
  * @return {Object}
*/
function getMatchEnding(item) {
  let endMatchTime;
  const timing = item.get('timing');
  if (!timing) return false;
  const stats = timing.get('stat');
  if (!stats) return false;
  const finalMatch = stats.toJS().find(element => element.Type === 'final_match_date_time');
  if (finalMatch && finalMatch.value) {
    endMatchTime = finalMatch.value;
  } else {
    const secondHalf = stats.toJS().find(element => element.Type === 'second_half_stop');
    endMatchTime = secondHalf.value;
  }
  return new Date(endMatchTime);
}

/**
  * Sorts full matches by ending date/time.
  * @param {Object} items
  * @return {Object}
*/
function sortByMatchEnding(items) {
  return items.sort((a, b) => {
    if (a.get('status') === 'FULL' && b.get('status') === 'FULL') {
      const datetimeA = getMatchEnding(a);
      const datetimeB = getMatchEnding(b);
      return datetimeB - datetimeA;
    }
    return 0;
  });
}

/**
  * Sorts matches placing italian teams first.
  * @param {Object} games
  * @return {Object}
*/
function sortByItaTeams(games) {
  const italians = sortByDateTime(games.filter((game) => {
    if (italianTeams.includes(game.get('homeTeam').get('teamId'))
     || italianTeams.includes(game.get('awayTeam').get('teamId'))) {
      return fromJS(game);
    }
    return null;
  }));

  const strangers = sortByDateTime(games.filter((game) => {
    if (!italianTeams.includes(game.get('homeTeam').get('teamId'))
     && !italianTeams.includes(game.get('awayTeam').get('teamId'))) {
      return game;
    }
    return null;
  }));

  return italians.concat(strangers);
}

function groupBy(xs, key) {
  return xs.reduce((rv, x) => {
    (rv[x.get(key)] = rv[x.get(key)] || []).push(x);
    return rv;
  }, {});
}

/**
  * Groups matches by days and sorts each day setting italian teams first.
  * @param {Object} games
  * @return {Object}
*/
function sortByDataTeams(games, reverse = false) {
  const gamesSortedbyDate = [];
  let resulGamesSorted = [];
  const gamesByDateDay = groupBy(games, 'dateDay');
  const sortedGamesByDateDay = Object.entries(gamesByDateDay).sort();
  if (reverse === true) sortedGamesByDateDay.reverse();
  sortedGamesByDateDay.forEach((item) => {
    resulGamesSorted = sortByItaTeams(sortByMatchEnding(item[1]));
    gamesSortedbyDate.push(...resulGamesSorted);
  });
  return fromJS(gamesSortedbyDate);
}

/**
  * Returns true if match ended less than 45 minutes ago.
  * @param {Object} timing
  * @return {Boolean}
*/
function withinFortyFive(timing) {
  let finalMatchTime;
  let secondHalfMatchTime;
  const minutesMatchPlus = 45;
  for (const value of timing) {
    if (value.Type === 'final_match_date_time') {
      finalMatchTime = new Date(value.value);
    }
    if (value.Type === 'second_half_stop') {
      secondHalfMatchTime = new Date(value.value);
    }
  }

  const endMatchTime = finalMatchTime || secondHalfMatchTime;
  const endMatchTimeNumeric = Math.round(endMatchTime.getTime());
  const endDateMatchPlus = new Date(endMatchTimeNumeric + (minutesMatchPlus * 60000));
  const now = new Date();
  // const now = new Date('August 06, 2020 23:59:00'); // used to simulate date and set it as "now" - use LOCAL time here
  const nowDate = Math.round(now.getTime());
  const endMatchPlusNumeric = Math.round(endDateMatchPlus.getTime());

  if (nowDate > endMatchTimeNumeric && nowDate <= endMatchPlusNumeric) {
    return true;
  } else {
    return false;
  }
}

/**
  * Manages matches sorting by status.
  * @param {Object} games
  * @return {Object}
*/
function cupsSorting(games) {
  const gamesSorted = [];
  let live = [];
  let prematch = [];
  let delayed = [];
  let postponed = [];
  let abandoned = [];
  let suspended = [];
  let fullAfter45 = [];
  let fullWithin45 = [];
  let full = [];
  const afterlive = [];
  const totalfull = [];
  const gamesByStatus = groupBy(games, 'status');

  Object.entries(gamesByStatus).forEach((item) => {
    switch (item[0]) {
      case 'LIVE':
        return live = sortByItaTeams(sortByDateTime(item[1]));
      case 'PREMATCH':
        return prematch = sortByDataTeams(item[1]);
      case 'FULL':
        [, full] = item;
        return full;
      case 'DELAYED':
        return delayed = sortByDateTime(item[1]);
      case 'POSTPONED':
        return postponed = sortByDateTime(item[1]);
      case 'ABANDONED':
        return abandoned = sortByDateTime(item[1]);
      case 'SUSPENDED':
        return suspended = sortByDateTime(item[1]);
      default:
        return [];
    }
  });
  if (full) {
    full.forEach((el) => {
      const timing = el.get('timing');
      const stat = timing.get('stat');
      if (stat && !withinFortyFive(stat.toJS())) {
        totalfull.push(el);
      } else {
        afterlive.push(el);
      }
    });
    fullWithin45 = sortByItaTeams(sortByMatchEnding(afterlive));
    fullAfter45 = sortByDataTeams(totalfull, true);
  }
  gamesSorted.push(...live, ...fullWithin45, ...prematch, ...fullAfter45);
  gamesSorted.push(...delayed, ...postponed, ...abandoned, ...suspended);
  return fromJS(gamesSorted);
}

function setStatusCalendar(calendar) {
  if (calendar && calendar.get('games') && calendar.get('games').last()) {
    const currentDay = new Date().getTime();
    calendar = calendar.set('status', 'LIVE');
    let firstMatchDay;
    let lastMatchDay;
    const firstGame = calendar.get('games').first();
    if (firstGame.get('matches') && firstGame.get('matches').first() && firstGame.get('matches').first().get('date')) {
      firstMatchDay = firstGame.get('matches').first().get('date');
      firstMatchDay = new Date(firstMatchDay).getTime();
    }
    const lastGame = calendar.get('games').last();
    if (lastGame.get('matches') && lastGame.get('matches').last() && lastGame.get('matches').last().get('date')) {
      lastMatchDay = lastGame.get('matches').last().get('date');
      lastMatchDay = new Date(lastMatchDay).getTime();
    }
    if (firstMatchDay && ((currentDay + 3600000) < firstMatchDay)) {
      calendar = calendar.set('status', 'PREMATCH');
    }
    if (lastMatchDay && ((currentDay + 10800000) > lastMatchDay)) {
      calendar = calendar.set('status', 'FULL');
    }
  }
  return calendar;
}

/**
  * Sets calendar custom sorting
  * @param {Object} calendar
  * @return {Object}
*/
function calendarMatchStatus(calendar, status) {
  let returnedCalendar;
  if (status === types.PREMATCH_STATUS && calendar && calendar.get('games')) {
    returnedCalendar = calendar.set(
      'games',
      calendar.get('games').map(day =>
        day.set(
          'matches',
          day.get('matches')
            .filter(match => match.get('status') === status)
            .sortBy(match => match.get('date')),
        )),
    );
  }
  if (status === types.LIVE_STATUS && calendar && calendar.get('games')) {
    returnedCalendar = calendar.set('games', calendar.get('games').map(day => (day.set('matches', day.get('matches')
      .filter(match => match.get('status') === status)))));
  }
  if (status === types.AFTER_LIVE_STATUS && calendar && calendar.get('games')) {
    // Within 45 minutes
    returnedCalendar = calendar.set('games', calendar.get('games').map(day => (day.set('matches', day.get('matches')
      .filter(match => (match.get('status') === types.FULL_STATUS && withinFortyFive(match.get('timing').get('stat').toJS())))))));
  }
  if (status === types.FULL_STATUS && calendar && calendar.get('games')) {
    // After 45 minutes
    const sortedGames = calendar.get('games').map((day) => {
      const dayMatches = day.get('matches');
      const sortedDayMatches = dayMatches.filter((match) => {
        const afterFourtyFive = match.get('status') === status && !withinFortyFive(match.get('timing').get('stat').toJS());
        return afterFourtyFive;
      });
      const invertedDayMatches = sortedDayMatches.reverse();
      return day.set('matches', invertedDayMatches);
    });
    const invertedGames = sortedGames.reverse();
    returnedCalendar = calendar.set('games', invertedGames);
  }
  if (status === types.DELAYED_STATUS && calendar && calendar.get('games')) {
    returnedCalendar = calendar.set('games', calendar.get('games').map(day => (day.set('matches', day.get('matches')
      .filter(match => match.get('status') === status).reverse()))));
  }
  if (status === types.POSTPONED_STATUS && calendar && calendar.get('games')) {
    returnedCalendar = calendar.set('games', calendar.get('games').map(day => (day.set('matches', day.get('matches')
      .filter(match => match.get('status') === status).reverse()))));
  }
  if (status === types.ABANDONED_STATUS && calendar && calendar.get('games')) {
    returnedCalendar = calendar.set('games', calendar.get('games').map(day => (day.set('matches', day.get('matches')
      .filter(match => match.get('status') === status).reverse()))));
  }
  if (status === types.SUSPENDED_STATUS && calendar && calendar.get('games')) {
    returnedCalendar = calendar.set('games', calendar.get('games').map(day => (day.set('matches', day.get('matches')
      .filter(match => match.get('status') === status).reverse()))));
  }
  return returnedCalendar;
}

/**
  * Sets cups calendar custom sorting
  * @param {Object} calendar
  * @return {Object}
*/
function cupCalendarMatchStatus(calendar) {
  let returnedCalendar;

  if (calendar && calendar.get('games')) {
    returnedCalendar = calendar.set('games', cupsSorting(calendar.get('games')));
  }
  return returnedCalendar;
}

export default function calendarReducer(state = initialState, action) {
  switch (action.type) {
    case types.GET_DAY_CALENDAR_SUCCESS:
    case types.GET_CALENDAR_SUCCESS:
    case types.GET_RUGBY_CALENDAR_SUCCESS:
      return state.mergeIn(['calendars'], { [action.widgetId]: setStatusCalendar(fromJS(action.data)) });
    case types.GET_CALENDAR_CORRIERE_SUCCESS:
    case types.GET_DAY_CALENDAR_CORRIERE_SUCCESS:
    case types.GET_CALENDAR_GAZZANET_SUCCESS:
      return state.set('calendarPre', calendarMatchStatus(fromJS(action.data), types.PREMATCH_STATUS))
        .set('calendarLive', calendarMatchStatus(fromJS(action.data), types.LIVE_STATUS))
        .set('calendarAfterLive', calendarMatchStatus(fromJS(action.data), types.AFTER_LIVE_STATUS))
        .set('calendarFull', calendarMatchStatus(fromJS(action.data), types.FULL_STATUS))
        .set('calendarDelay', calendarMatchStatus(fromJS(action.data), types.DELAYED_STATUS))
        .set('calendarPost', calendarMatchStatus(fromJS(action.data), types.POSTPONED_STATUS))
        .set('calendarAbandoned', calendarMatchStatus(fromJS(action.data), types.ABANDONED_STATUS))
        .set('calendarSuspended', calendarMatchStatus(fromJS(action.data), types.SUSPENDED_STATUS))
        .set('isCalendarLoaded', true);
    case types.GET_DAY_CUP_CALENDAR_CORRIERE_SUCCESS:
    case types.GET_CUP_CALENDAR_CORRIERE_SUCCESS:
      return state.set('calendar', cupCalendarMatchStatus(fromJS(action.data), action.urlParams)).set('isCalendarLoaded', true);
    case types.GET_TEAM_CALENDAR_SUCCESS:
      return state.mergeIn(['calendars'], { [action.widgetId]: fromJS(action.data) });
    case types.GET_RUGBY_TABBAR_SUCCESS:
      return state.mergeIn(['cupTabbars'], { [action.widgetId]: fromJS(action.data) });
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/calendar.js